import type { EditorCommand, EditorInstance } from "@quire/editor";
import { useEffect, useRef, useState } from "preact/hooks";
import type { App } from "../app.ts";
import { useAppState } from "./hooks.ts";
import { IconButton } from "./icon.tsx";
import type { IconName } from "./icon-paths.ts";

const onMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
/** "Mod+B" written for this system. */
const keyName = (shortcut: string) => shortcut.replace("Mod", onMac ? "Cmd" : "Ctrl");

export function Bar({ app, editor }: { app: App; editor: EditorInstance | null }) {
  const s = useAppState(app);
  const has = Boolean(s.current);
  const dark = app.effectiveTheme() === "dark";
  const commands = editor?.commands ?? [];
  const bar = useRef<HTMLDivElement>(null);
  const [asking, setAsking] = useState<EditorCommand | null>(null);

  const visible = commands.length > 0 || Boolean(asking) || Boolean(s.altAsk);

  // The page keeps clear of the bar, so it needs to know how tall it is right now.
  useEffect(() => {
    const el = bar.current;
    const root = document.documentElement.style;
    if (!el || !visible) {
      root.setProperty("--bar-height", "0px");
      return;
    }
    const publish = () => root.setProperty("--bar-height", `${el.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  // A shortcut such as Ctrl+K asks the same question the Link button does.
  useEffect(() => {
    const ask = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      const command = commands.find((c) => c.id === id && c.prompt);
      if (command) setAsking(command);
    };
    window.addEventListener("quire:prompt", ask);
    return () => window.removeEventListener("quire:prompt", ask);
  }, [commands]);

  // A command that needs a file (an image) opens the file chooser, keeps the file, then runs.
  const picker = useRef<HTMLInputElement>(null);
  const picking = useRef<EditorCommand | null>(null);
  const chooseFile = (command: EditorCommand) => {
    picking.current = command;
    if (picker.current) {
      picker.current.accept = command.file ?? "";
      picker.current.click();
    }
  };
  const chosen = async (file: File | undefined) => {
    const command = picking.current;
    if (picker.current) picker.current.value = "";
    if (!file || !command || !editor) return;
    const markdown = await app.addImage(file);
    if (markdown) editor.runCommand(command.id, markdown);
  };

  // A question belongs to one editor and one document.
  useEffect(() => setAsking(null), [editor, s.current?.path]);

  return (
    <div
      class="island bottom bar"
      ref={bar}
      role="toolbar"
      aria-label="Formatting"
      hidden={!visible}
    >
      <input
        ref={picker}
        class="visually-hidden"
        type="file"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => chosen((e.currentTarget as HTMLInputElement).files?.[0])}
      />
      {s.altAsk ? (
        <PromptRow
          prompt={{ label: "Picture description", placeholder: "[description]" }}
          initial={s.altAsk.initial}
          allowEmpty
          done={(value) => app.answerAlt(value)}
        />
      ) : asking && editor ? (
        <PromptRow
          prompt={asking.prompt!}
          initial={asking.id === "link" ? (s.caret.link ?? "") : ""}
          done={(value) => {
            if (value !== null) editor.runCommand(asking.id, value);
            setAsking(null);
          }}
        />
      ) : (
        grouped(commands).map((group, i) => (
          <div class="island-group" key={group[0]!.id}>
            {i > 0 && <span class="rule" />}
            {group.map((c) => {
              const on = s.caret.active.includes(c.id);
              return (
                <IconButton
                  key={c.id}
                  icon={ICON_FOR[c.id] ?? "info"}
                  label={c.label}
                  tip={c.shortcut ? `${c.label} (${keyName(c.shortcut)})` : c.label}
                  on={TOGGLES.has(c.id) && on}
                  disabled={!has}
                  aria-pressed={TOGGLES.has(c.id) ? on : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    c.file ? chooseFile(c) : c.prompt ? setAsking(c) : editor?.runCommand(c.id)
                  }
                />
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}

/** Which drawing each command uses. */
const ICON_FOR: Record<string, IconName> = {
  bold: "bold",
  italic: "italic",
  code: "code",
  "heading-2": "heading",
  "bullet-list": "list",
  "numbered-list": "list-ordered",
  "task-list": "list-checks",
  quote: "quote",
  link: "link",
  table: "table",
  image: "image",
  undo: "undo",
  redo: "redo",
};

/** The buttons in groups by what they do to the text, a thin rule between groups. */
const GROUPS = [
  ["bold", "italic", "code"],
  ["heading-2", "bullet-list", "numbered-list", "task-list", "quote"],
  ["link", "table", "image"],
  ["undo", "redo"],
];

function grouped(commands: EditorCommand[]): EditorCommand[][] {
  const used = new Set<string>();
  const out = GROUPS.map((ids) =>
    ids.flatMap((id) => {
      const c = commands.find((x) => x.id === id);
      if (c) used.add(id);
      return c ? [c] : [];
    }),
  ).filter((g) => g.length > 0);
  const rest = commands.filter((c) => !used.has(c.id));
  if (rest.length > 0) out.push(rest);
  return out;
}

/** Buttons that are on or off at the caret. */
const TOGGLES = new Set([
  "bold",
  "italic",
  "code",
  "link",
  "heading-2",
  "bullet-list",
  "numbered-list",
  "task-list",
  "quote",
]);

/** One question in the bar, such as a link address. `done(null)` means cancelled. */
function PromptRow({
  prompt,
  initial = "",
  allowEmpty = false,
  done,
}: {
  prompt: { label: string; placeholder: string };
  initial?: string;
  /** An empty answer is an answer (it means "use the suggestion"), not a cancel. */
  allowEmpty?: boolean;
  done: (value: string | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
    // Only a suggested answer is selected; selecting an empty box could swallow what is typed next.
    if (initial) input.current?.select();
  }, []);
  return (
    <form
      class="prompt"
      onSubmit={(e) => {
        e.preventDefault();
        const value = input.current?.value.trim() ?? "";
        done(value === "" && !allowEmpty ? null : value);
      }}
    >
      <label class="visually-hidden" for="bar-prompt">
        {prompt.label}
      </label>
      <input
        id="bar-prompt"
        ref={input}
        class="field"
        type="text"
        placeholder={prompt.placeholder}
        defaultValue={initial}
        autocomplete="off"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            done(null);
          }
        }}
      />
      <button class="button primary" type="submit">
        Apply
      </button>
      <button class="button" type="button" onClick={() => done(null)}>
        Cancel
      </button>
    </form>
  );
}
